import type { Workgroup, WorkgroupHuddle, WorkgroupMemberDirectoryEntry, WorkgroupCategory } from '@easyflow/shared';

export type { Workgroup, WorkgroupHuddle, WorkgroupMemberDirectoryEntry, WorkgroupCategory };

export interface WorkgroupMember {
  user_id: string;
  role: 'admin' | 'member';
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  joined_at: string;
}

export interface WorkgroupFile {
  id: string;
  filename: string;
  size: number;
  mime_type: string;
  storage_path: string;
  created_at: string;
  uploaded_by: string;
  uploader_first_name?: string | null;
  uploader_last_name?: string | null;
}

export interface FeedPollOption {
  id: string;
  option_text: string;
  vote_count: number;
}

export interface FeedPoll {
  id: string;
  question: string;
  is_multiple_choice: boolean;
  is_anonymous: boolean;
  closes_at: string | null;
  options: FeedPollOption[];
  total_votes: number;
  user_votes: string[];
}

export interface FeedAttachment {
  id: string;
  filename: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
}

export interface FeedPost {
  id: string;
  author_id: string;
  author_first_name?: string | null;
  author_last_name?: string | null;
  author_avatar_url?: string | null;
  post_type: string;
  title?: string | null;
  content_html?: string | null;
  content_text?: string | null;
  is_pinned: boolean;
  created_at: string;
  reactions_count?: number;
  comments_count?: number;
  my_reaction?: string | null;
  poll?: FeedPoll | null;
  attachments?: FeedAttachment[];
}

export const CATEGORY_LABELS: Record<WorkgroupCategory, string> = {
  project: 'Progetto',
  team: 'Team',
  client: 'Cliente',
  initiative: 'Iniziativa',
};

export const CATEGORY_COLORS: Record<WorkgroupCategory, string> = {
  project: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
  team: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  client: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
  initiative: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
};

export const THEME_COLORS = ['blue', 'emerald', 'purple', 'orange', 'pink', 'cyan', 'amber', 'indigo', 'red', 'teal'] as const;
export type ThemeColor = typeof THEME_COLORS[number];

export function getThemeGradient(theme?: string | null, name?: string | null): string {
  const map: Record<string, string> = {
    blue: 'from-blue-600 to-indigo-700',
    emerald: 'from-emerald-600 to-teal-700',
    purple: 'from-purple-600 to-fuchsia-700',
    orange: 'from-orange-500 to-red-600',
    pink: 'from-pink-500 to-rose-600',
    cyan: 'from-cyan-500 to-blue-600',
    amber: 'from-amber-500 to-orange-600',
    indigo: 'from-indigo-600 to-violet-700',
    red: 'from-red-600 to-rose-700',
    teal: 'from-teal-500 to-emerald-600',
  };
  if (theme && map[theme]) return map[theme]!;
  // fallback hash-based
  const palette = Object.values(map);
  const seed = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return palette[seed % palette.length] ?? palette[0]!;
}

export function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}
